import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const CreateDiagnosticTest = () => {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    gender: "", // NEW: Added gender field
    description: "",
    instruction: "",
    fastingRequired: false,
    homeCollectionAvailable: false,
    reportIn24Hrs: false,
    reportHour: "",
    precaution: ""
  });

  const [diagnosticsList, setDiagnosticsList] = useState([]);
  const [selectedDiagnostics, setSelectedDiagnostics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const navigate = useNavigate();

  // Fetch diagnostics list
  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        const token = localStorage.getItem("authToken");

        setLoadingDiagnostics(true);

        const response = await axios.get(
          "https://api.elthiumhealth.com/api/admin/alldiagnostics",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data?.data) {
          setDiagnosticsList(response.data.data);
        } else {
          setDiagnosticsList([]);
        }
      } catch (error) {
        console.error(
          "Error fetching diagnostics:",
          error.response?.data || error.message
        );

        setDiagnosticsList([]);
      } finally {
        setLoadingDiagnostics(false);
      }
    };

    fetchDiagnostics();
  }, []);

  // Gérer les changements de champ
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Gérer la sélection des diagnostics
  const handleDiagnosticChange = (diagnosticId) => {
    const currentDiagnostics = [...selectedDiagnostics];

    if (currentDiagnostics.includes(diagnosticId)) {
      setSelectedDiagnostics(currentDiagnostics.filter(id => id !== diagnosticId));
    } else {
      setSelectedDiagnostics([...currentDiagnostics, diagnosticId]);
    }
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.price || !formData.gender) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Test name, price and gender are required fields",
      });
      return;
    }

    // Validate gender
    if (!["Male", "Female", "Both"].includes(formData.gender)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Gender",
        text: "Please select a valid gender (Male, Female, or Both)",
      });
      return;
    }

    // Validate non-negative price
    if (parseFloat(formData.price) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Price",
        text: "Price cannot be negative",
      });
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");

      if (!token) {
        Swal.fire({
          icon: "warning",
          title: "Authentication Required",
          text: "Please log in again to continue.",
        });
        return;
      }

      // Prepare request data
      const requestData = {
        ...formData,
        price: parseFloat(formData.price),
        reportHour: formData.reportHour || undefined,
        diagnosticIds: selectedDiagnostics,
      };

      console.log("Sending data:", requestData);

      const response = await axios.post(
        "https://api.elthiumhealth.com/api/admin/create-test",
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201 || response.data?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Diagnostic test created successfully",
          showConfirmButton: false,
          timer: 1500,
        });

        // Reset form
        setFormData({
          name: "",
          price: "",
          gender: "",
          description: "",
          instruction: "",
          fastingRequired: false,
          homeCollectionAvailable: false,
          reportIn24Hrs: false,
          reportHour: "",
          precaution: "",
        });

        setSelectedDiagnostics([]);

        // Navigate to the test list
        setTimeout(() => {
          navigate("/diagtestlist");
        }, 1500);
      }
    } catch (error) {
      console.error(
        "Error creating diagnostic test:",
        error.response?.data || error.message
      );

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create diagnostic test";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser le formulaire
  const handleReset = () => {
    setFormData({
      name: "",
      price: "",
      gender: "", // NEW: Reset gender
      description: "",
      instruction: "",
      fastingRequired: false,
      homeCollectionAvailable: false,
      reportIn24Hrs: false,
      reportHour: "",
      precaution: ""
    });
    setSelectedDiagnostics([]);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-4xl mx-auto">
      <h3 className="text-xl font-bold mb-6 text-gray-800">Create New Diagnostic Test</h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Test Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Test Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Complete Blood Count (CBC), Lipid Profile"
            required
          />
        </div>

        {/* Price, Gender & Report Hour */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter price"
              min="0"
              step="0.01"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Enter 0 for free test</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Both">Both</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Hour (Optional)
            </label>
            <input
              type="text"
              name="reportHour"
              value={formData.reportHour}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., 24 hours, 48 hours, Same day"
            />
          </div>
        </div>

        {/* Checkboxes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
            <input
              type="checkbox"
              name="fastingRequired"
              checked={formData.fastingRequired}
              onChange={handleChange}
              className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-3 text-gray-700">
              <span className="font-medium">Fasting Required</span>
            </span>
          </label>

          <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
            <input
              type="checkbox"
              name="homeCollectionAvailable"
              checked={formData.homeCollectionAvailable}
              onChange={handleChange}
              className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-3 text-gray-700">
              <span className="font-medium">Home Collection</span>
            </span>
          </label>

          <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
            <input
              type="checkbox"
              name="reportIn24Hrs"
              checked={formData.reportIn24Hrs}
              onChange={handleChange}
              className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-3 text-gray-700">
              <span className="font-medium">Report in 24 Hours</span>
            </span>
          </label>
        </div>

        {/* Description & Instruction */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="4"
              placeholder="Describe the test details, what it measures, clinical significance..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instructions
            </label>
            <textarea
              name="instruction"
              value={formData.instruction}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="4"
              placeholder="Instructions for patients before taking the test..."
            />
          </div>
        </div>

        {/* Diagnostics Selection */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-gray-800">Assign to Diagnostics Centers (Optional)</h4>
            <span className="text-xs text-gray-500">
              {selectedDiagnostics.length} selected
            </span>
          </div>

          {loadingDiagnostics ? (
            <div className="text-center py-4">
              <div className="text-sm text-gray-500">Loading diagnostics...</div>
            </div>
          ) : diagnosticsList.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto p-2">
              {diagnosticsList.map((diagnostic) => (
                <label
                  key={diagnostic._id}
                  className="flex items-center p-3 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedDiagnostics.includes(diagnostic._id)}
                    onChange={() => handleDiagnosticChange(diagnostic._id)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    {diagnostic.name || "Unnamed Diagnostic"}
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-gray-500">
              No diagnostics centers available
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">Information</h4>
              <ul className="mt-2 text-xs text-blue-700 space-y-1">
                <li>• <span className="font-medium">Test Name</span>, <span className="font-medium">Price</span> and <span className="font-medium">Gender</span> are required fields</li>
                <li>• <span className="font-medium">Price</span> can be 0 for free tests</li>
                <li>• <span className="font-medium">Report Hour</span> is optional (e.g., "24 hours", "Same day")</li>
                <li>• Check relevant features based on test requirements</li>
                <li>• Assigning to diagnostics is optional - can be done later</li>
                <li>• <span className="font-medium">Gender</span> specifies if test is for Male, Female, or Both</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-between items-center pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Reset Form
            </button>

            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2 ${loading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
                } transition-colors`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Test...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  Create Test
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateDiagnosticTest;